import { motion } from 'framer-motion';
import HeroSlider from '../shared/HeroSlider';
import Button from '../shared/Button';
import { useWaitlist } from '../../context/WaitlistContext';
import { useLocationModal } from '../../context/LocationModalContext';

// Animation variants
const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const buttonVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
      delay: 0.3
    }
  }
};

export default function Hero() {
  const { openWaitlist } = useWaitlist();
  const { openLocationModal } = useLocationModal();
  return (
    <section className="h-auto md:h-screen flex flex-col justify-end pt-16 mt-12 md:mt-0">
      <div className="container mx-auto px-4">
        {/* Mobile layout: with title split across image */}
        <div className="block sm:hidden">
          {/* Image Container with title split across top */}
          <div className="aspect-[3/4] rounded-lg relative mb-6">
            {/* Title positioned to split across image top */}
            <motion.h1 
              className="text-3xl font-heading absolute top-[-2rem] left-0 z-20 pl-4"
              variants={textVariants}
              initial="hidden"
              animate="visible"
            >
              <span className="text-brand-navy block mb-1">AWARD-WINNING BREAKFAST</span>
              <span className="text-white block">HOMESTYLE TASTE</span>
            </motion.h1>
            
            <HeroSlider />
          </div>
          
          {/* Description text below image */}
          <div className="mb-6">
            <motion.p 
              className="text-brand-navy text-sm"
              variants={textVariants}
              initial="hidden"
              animate="visible"
            >
              From regional specialties to breakfast classics, our menu features hand-crafted tastes for everyone.
            </motion.p>
          </div>
          
          {/* Buttons - stacked */}
          <div className="space-y-3 mb-8">
            <motion.div
              variants={buttonVariants}
              initial="hidden"
              animate="visible"
              className="w-full"
            >
              <Button 
                href="/menu" 
                variant="solid" 
                color="navy"
                className="w-full"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                }
              >
                View our menu
              </Button>
            </motion.div>
            <motion.div
              variants={buttonVariants}
              initial="hidden"
              animate="visible"
              className="w-full"
            >
              <Button 
                onClick={openWaitlist}
                variant="outline" 
                color="navy"
                className="w-full"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                }
              >
                Join the waitlist
              </Button>
            </motion.div>
          </div>
        </div>
        
        {/* Desktop layout: original styling */}
        <div className="hidden sm:block relative">
          {/* Image Container with Rounded Top Corners */}
          <div className="h-[80dvh] rounded-t-lg relative">
            {/* Single H1 with both text lines for accessibility - positioned for inside/outside effect */}
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-[4.25rem] font-heading absolute top-[-4.25rem] left-0 z-20 pl-4"
              variants={textVariants}
              initial="hidden"
              animate="visible"
            >
              <span className="text-brand-navy block mb-2">AWARD-WINNING BREAKFAST</span>
              <span className="text-white block">HOMESTYLE TASTE</span>
            </motion.h1>
            
            {/* Slider */}
            <HeroSlider />
            
            {/* Subtext */}
            <div className="absolute top-8 right-8 text-right z-20">
              <motion.p 
                className="text-white text-base md:text-lg lg:text-xl max-w-[30rem]"
                variants={textVariants}
                initial="hidden"
                animate="visible"
              >
                From regional specialties to breakfast classics, our menu features hand-crafted tastes for everyone.
              </motion.p>
            </div>
            
            {/* Buttons */}
            <div className="absolute bottom-8 right-8 z-20">
              <div className="flex space-x-4">
                <motion.div
                  variants={buttonVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Button 
                    href="/menu" 
                    variant="solid" 
                    color="navy"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    }
                  >
                    View our menu
                  </Button>
                </motion.div>
                <motion.div
                  variants={buttonVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Button 
                    onClick={openWaitlist}
                    variant="glass" 
                    color="white"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    }
                  >
                    Join the waitlist
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
